import { DashboardLayout } from '@/components/layouts/DashboardLayout';
import UserAvatar from '@/components/avatar/UserAvatar';
import { JobApplicationStatusBadge } from '@/components/job/JobApplicationStatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useProfile } from '@/contexts/ProfileContext';
import { useApiClient } from '@/lib/api';
import { getLatestJobApplicationStatus, isActiveJob, sortJobsByPriorityAndRecency } from '@/lib/job-status';
import { AlertTriangle, Clock, Mic, Play } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { api } = useApiClient();
  const { user } = useAuth();
  const { hasProfileData, isLoading: profileLoading } = useProfile();
  const [stats, setStats] = useState({
    totalJobs: 0,
    completedInterviews: 0,
    pendingInterviews: 0,
    profileReady: "",
  });
  const [jobs, setJobs] = useState<any[]>([]);
  const [todosOverdue, setTodosOverdue] = useState<any[]>([]);
  const [todosDueSoon, setTodosDueSoon] = useState<any[]>([]);
  const [pitches, setPitches] = useState<any[]>([]);
  const [recentInterviews, setRecentInterviews] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [readinessChecked, setReadinessChecked] = useState(false);
  const [now, setNow] = useState<Date>(new Date());

  // keep a ticking clock for countdowns (updates every minute)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    api.checkReadiness()
      .then((res: any) => {
        if (!res.is_ready) {
          navigate("/getting-started", { replace: true });
          console.log("boop");
        } else {
          // User is ready — stay on dashboard
          setReadinessChecked(true);
        }
      })
      .catch((err: any) => {
        console.error("Readiness check failed", err);
        setReadinessChecked(true); // Allow dashboard if error
      });
  }, [api, navigate]);

  useEffect(() => {
    if (!readinessChecked) return;

    const fetchDashboardData = async () => {
      if (!hasProfileData) {
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        const overview = await api.getDashboardOverview(48, 5);

        setStats({
          totalJobs: overview?.stats?.total_jobs || 0,
          completedInterviews: overview?.stats?.completed_interviews || 0,
          pendingInterviews: overview?.stats?.pending_interviews || 0,
          profileReady: overview?.stats?.profile_ready || "A-OK",
        });

        const overviewJobs = overview?.jobs || [];
        const recentPitches = [...(overview?.pitches || [])]
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);
        const recentInts = [...(overview?.recent_interviews || [])]
          .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 5);

        setJobs(sortJobsByPriorityAndRecency(overviewJobs));
        setTodosOverdue(overview?.todos_overdue || []);
        setTodosDueSoon(overview?.todos_due_soon || []);
        setPitches(recentPitches);
        setRecentInterviews(recentInts);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [api, hasProfileData, readinessChecked]);



  // helpers
  const upcomingInterviews = useMemo(() => {
    const all = (jobs || []).flatMap((j: any) => {
      const a = Array.isArray(j.job_interviews) ? j.job_interviews : [];
      const b = Array.isArray(j.interviews) ? j.interviews : [];
      return [...a, ...b].map((iv: any) => ({ ...iv, _job: j }));
    });
    const future = all.filter((iv: any) => {
      const d = iv?.interview_date ? new Date(iv.interview_date) : null;
      return d && d.getTime() > now.getTime();
    });
    future.sort((a: any, b: any) => new Date(a.interview_date).getTime() - new Date(b.interview_date).getTime());
    return future;
  }, [jobs, now]);

  const upcomingInterview = useMemo(() => upcomingInterviews[0] || null, [upcomingInterviews]);

  // Friendly relative time for interview date (Today, In 3 hours, In 3 days, etc.)
  const relativeInterviewTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    const target = new Date(dateStr);
    const diffMs = target.getTime() - now.getTime();
    const diffMinutes = Math.round(diffMs / (1000 * 60));
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    
    // If it's in the past, show "Today" as fallback
    if (diffMs < 0) return 'Today';
    
    // Helper to get start of day
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const todayStart = startOfDay(now);
    const targetStart = startOfDay(target);
    const isToday = targetStart.getTime() === todayStart.getTime();
    
    // Check if it's tomorrow (next calendar day)
    const tomorrowStart = new Date(todayStart);
    tomorrowStart.setDate(tomorrowStart.getDate() + 1);
    const isTomorrow = targetStart.getTime() === tomorrowStart.getTime();
    
    if (isToday) {
      // Less than 1 hour away: show minutes
      if (diffMinutes < 60) {
        return diffMinutes <= 1 ? 'In 1 minute' : `In ${diffMinutes} minutes`;
      }
      // Less than or equal to 6 hours away: show hours
      if (diffHours <= 6) {
        return diffHours === 1 ? 'In 1 hour' : `In ${diffHours} hours`;
      }
      // More than 6 hours away but still today: just show "Today"
      return 'Today';
    }
    
    // Tomorrow
    if (isTomorrow) return 'Tomorrow';
    
    // Within a week: show days
    if (diffDays < 7) return `In ${diffDays} days`;
    
    // Weeks
    const weeks = Math.round(diffDays / 7);
    return weeks === 1 ? 'In 1 week' : `In ${weeks} weeks`;
  };

  const priorityActions = useMemo(() => {
    const items: { label: string; to: string }[] = [];

    // Check for upcoming real world interviews - 
    // These should be the top priority
    if (upcomingInterview) {
      const j = upcomingInterview._job;
      const jobMessages = (j) => [
        `Prepare for your ${j?.title} interview at ${j?.company}`,
        `Coming up soon: ${j?.title} interview at ${j?.company}`,
        `You’ve got an interview at ${j?.company} for ${j?.title}`,
        `Your next step: ${j?.title} interview with ${j?.company}`,
        `Getting ready for a big opportunity at ${j?.company}`,
        `Interview scheduled at ${j?.company} for ${j?.title}`,
        `Sharpen your pitch — ${j?.company} awaits`,
        `Big moment ahead: ${j?.company}, ${j?.role} interview`,
        `Next opportunity: ${j?.title} at ${j?.company}`,
        `All set? ${j?.company} interview coming up`,
        `Get ready for your interview with ${j?.company}`,
        `Your next challenge: ${j?.title} interview at ${j?.company}`,
        `Step closer to your goal — interview at ${j?.company}`,
        `Heads up: ${j?.company} interview on the horizon`,
        `Exciting! Your interview — ${j?.title} with ${j?.company} - is soon!`,
        `Time to shine: interview at ${j?.company}`
      ];

      items.push({ label: jobMessages(j)[Math.floor(Math.random() * jobMessages(j).length)], to: `/jobs/${j?.id}` });
    }


    // Find overdue todos
    const overdue = (todosOverdue || [])[0];
    if (overdue) {
      const todoMessages = (t) => [
        `Don’t forget: ${t?.title} is overdue`,
        `Catch up on: ${t?.title}`,
        `Still pending: ${t?.title}`,
        `Overdue task: ${t?.title}`,
        `Time to wrap up: ${t?.title}`,
        `Let’s finish: ${t?.title}`,
        `Needs attention: ${t?.title}`,
        `Reminder — ${t?.title} is waiting`,
        `Take care of: ${t?.title}`,
        `Quick win: complete ${t?.title}`,
        `Clear your list: ${t?.title}`,
        `Past due — handle ${t?.title} soon`,
        `Bring it home: ${t?.title}`,
        `Tackle this next: ${t?.title}`,
        `Make progress by finishing ${t?.title}`
      ];

      items.push({ label: todoMessages(overdue)[Math.floor(Math.random() * todoMessages(overdue).length)], to: '/todos' });
    }

    const dueSoon = (todosDueSoon || [])[0];
    if (dueSoon) {
      const todoMessages = (t) => [
        `Don’t forget: ${t?.title} is overdue`,
        `Catch up on: ${t?.title}`,
        `Still pending: ${t?.title}`,
        `Overdue task: ${t?.title}`,
        `Time to wrap up: ${t?.title}`,
        `Let’s finish: ${t?.title}`,
        `Needs attention: ${t?.title}`,
        `Reminder — ${t?.title} is waiting`,
        `Take care of: ${t?.title}`,
        `Quick win: complete ${t?.title}`,
        `Clear your list: ${t?.title}`,
        `Past due — handle ${t?.title} soon`,
        `Bring it home: ${t?.title}`,
        `Tackle this next: ${t?.title}`,
        `Make progress by finishing ${t?.title}`,
        `Coming up soon: ${t?.title}`,
        `Due soon — stay ahead: ${t?.title}`,
        `Almost time: ${t?.title}`,
        `On your radar: ${t?.title}`,
        `Don’t miss this one: ${t?.title}`,
        `Prepare to finish: ${t?.title}`,
        `Due shortly: ${t?.title}`,
        `Get ready to tackle: ${t?.title}`,
        `Plan a moment for: ${t?.title}`,
        `Next on your list: ${t?.title}`,
        `Knock this out soon: ${t?.title}`,
        `Approaching deadline: ${t?.title}`,
        `Be proactive: ${t?.title} is coming up`,
        `Stay ahead with: ${t?.title}`,
        `Time’s coming — ${t?.title}`
      ];

      items.push({ label: todoMessages(dueSoon)[Math.floor(Math.random() * todoMessages(dueSoon).length)], to: '/todos' });
    }

    if (items.length === 0 && (jobs || []).length > 0) {
      const j = jobs[0];
      const reviewJobsMessages = (j) => [
        `Take another look at ${j?.title} at ${j?.company}`,
        `Review your saved job: ${j?.title} at ${j?.company}`,
        `Thinking about ${j?.company}? Check out the ${j?.title} role again`,
        `Stay sharp — revisit ${j?.title} at ${j?.company}`,
        `Give some thought to ${j?.title} at ${j?.company}`,
        `Is ${j?.title} at ${j?.company} still a top pick?`,
        `Double-check if ${j?.company}’s ${j?.title} is the right fit`,
        `Keep ${j?.company} on your radar — ${j?.title}`,
        `Worth another glance: ${j?.title} at ${j?.company}`,
        `See if ${j?.company}’s ${j?.title} aligns with your goals`,
        `Revisit ${j?.title} at ${j?.company} and refine your plan`,
        `How does ${j?.title} at ${j?.company} look today?`,
        `Spend a minute reviewing ${j?.company} — ${j?.title}`,
        `Check back in on ${j?.title} at ${j?.company}`,
        `Your saved role: ${j?.title} at ${j?.company} deserves another look`
      ];
      items.push({ label: reviewJobsMessages(j)[Math.floor(Math.random() * reviewJobsMessages(j).length)], to: `/jobs/${j.id}` });
    }

    // Add a message to practice pitching themselves
    const randomJob = (jobs) => {
      let j = null;
      if (Array.isArray(jobs) && jobs.length > 0) {
        j = jobs[Math.floor(Math.random() * jobs.length)];
      }
      return j;
    };
    const pitchMessages = (j) => {
      if (!j) {
        // General self-pitch encouragement
        return [
          "Time to sharpen your story — record a pitch!",
          "Practice your elevator pitch and boost your confidence.",
          "Your voice matters — try a quick self-intro.",
          "Polish your personal pitch today.",
          "Ready to impress? Rehearse your pitch.",
          "Refine your story — 60 seconds is all it takes.",
          "Give your elevator pitch another go — practice makes perfect.",
          "Stay prepared: practice how you introduce yourself.",
          "Try a quick pitch to highlight your strengths.",
          "Level up your self-presentation with a new pitch.",
          "First impressions are everything. Practice the \"you\" pitch"
        ];
      } else {
        // Job-targeted pitch encouragement
        return [
          `Craft your pitch for ${j?.title} at ${j?.company}`,
          `Prepare a 60-second intro tailored to ${j?.company}`,
          `How would you sell yourself for ${j?.title}? Practice now.`,
          `Quick pitch time — imagine you’re in the lobby at ${j?.company}`,
          `Highlight your strengths for ${j?.title} at ${j?.company}`,
          `Tailor your story: ${j?.title} interview coming up?`,
          `Practice how you'd introduce yourself to ${j?.company}`,
          `Get pitch-ready for ${j?.title} at ${j?.company}`,
          `Sharpen your introduction for ${j?.title} at ${j?.company}`,
          `Make your case: why you fit ${j?.title} at ${j?.company}`
        ];
      }
    };
    const pitchJob = randomJob(jobs);
    items.push({ label: pitchMessages(pitchJob)[Math.floor(Math.random() * pitchMessages(pitchJob).length)], to: '/pitch/new?jobId=' + pitchJob?.id });

    const jobHuntEncouragementMessages = () => [
      "No jobs yet? Start your hunt today — HiredCoach is here to guide you.",
      "Kick off your job search! HiredCoach can track and support every step.",
      "Ready for the next chapter? Add a job and let HiredCoach help you prepare.",
      "Start exploring opportunities — HiredCoach will keep you focused and interview-ready.",
      "Begin your job hunt now. HiredCoach has the tools to make it easier.",
      "Add your first job to track progress and unlock tailored practice.",
      "The journey starts here — find a role and let HiredCoach coach you along the way.",
      "Looking for opportunities? HiredCoach will support you with resumes, pitches, and practice.",
      "Take the first step in your career move — add a job to get started.",
      "Your next opportunity is out there — HiredCoach will help you chase it down."
    ];

    if (!jobs || jobs.length === 0) {
      items.push({ label: jobHuntEncouragementMessages()[Math.floor(Math.random() * jobHuntEncouragementMessages().length)], to: '/jobs' });
    }

    // Return the top 5 items in the order we defined them
    return items.slice(0, 5);
  }, [upcomingInterview, todosOverdue, todosDueSoon, jobs]);

  const activeJobs = useMemo(() => {
    return sortJobsByPriorityAndRecency((jobs || []).filter((job: any) => isActiveJob(job))).slice(0, 6);
  }, [jobs]);


  // Personalized greeting header message
  const getScoreLabel = (score: number) => {
    if (score > 3.9) return "S"
    else if (score > 3.4) return "A"
    else if (score > 2.9) return "B"
    else if (score > 1.9) return "C"
    else if (score > 1.4) return "D"
    else return "F"
  };

  const getGradeTextColor = (grade: number) => {
    const label = getScoreLabel(grade);
    const gradeTextColors = {
      S: 'text-amber-900 font-bold drop-shadow-sm',
      A: 'text-green-900',
      B: 'text-green-700',
      C: 'text-yellow-700',
      D: 'text-red-700',
      F: 'text-red-900',
    };
    return gradeTextColors[label];
  };

  const name = (user?.firstName || '').trim();
  const headerMessage = useMemo(() => {
    const n = name || null;
    const msgs = [
      n ? `Hey ${n}, ready to kick off your job hunt?` : 'Ready to kick off your job hunt?',
      n ? `Let’s make progress today, ${n}.` : 'Let’s make progress today.',
      n ? `${n} - your next opportunity is waiting.` : 'Your next opportunity is waiting.',
      n ? `Keep going, ${n} - you’ve got this.` : 'Keep going - you’ve got this.',
      n ? `Good to see you, ${n}! How about tackling your next step?` : 'Good to see you! How about tackling your next step?',
      n ? `What’s up, ${n}? Let’s get you closer to your goals.` : 'Let’s get you closer to your goals.',
      n ? `Don’t stop now, ${n} - you’re making great progress.` : 'Don’t stop now - you’re making great progress.',
      n ? `${n} - small steps add up. Let’s take one today.` : 'Small steps add up. Let’s take one today.',
      n ? `HiredCoach has your back, ${n}. Let's do this.` : 'HiredCoach has your back.'
    ];
    return msgs[Math.floor(Math.random() * msgs.length)];
  }, [name]);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header with logo */}
        <div className="rounded-2xl bg-gradient-to-r from-[#f7f9fb] via-[#eaf3fa] to-[#f2f5f9] border border-blue-50 p-5 sm:p-8 flex items-center justify-center gap-4">
          <UserAvatar user={user} className="w-24 h-24 sm:w-20 sm:h-20 border-0" fallback="logo" />
          <div className="min-w-0 text-center">
            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">{headerMessage}</h1>
          </div>
        </div>

        {/* Quick Actions and Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">


          <Card className="h-full">
            <CardHeader>
              <CardTitle className="text-xl">Today’s Focus</CardTitle>
              <CardDescription className="text-base">Take one step at a time. Here's a few suggestions.</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-2 text-gray-500">Loading…</div>
              ) : priorityActions.length > 0 ? (
                <ul className="space-y-3">
                  {priorityActions.map((a, idx) => (
                    <li key={idx} className="overflow-hidden">
                      <Link to={a.to} className="flex items-center p-4 bg-white rounded-lg border shadow-sm hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200 min-w-0">
                        <span className="font-medium truncate text-[15px]">{a.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-gray-500">All clear. Nice work!</div>
              )}
            </CardContent>
          </Card>

          {upcomingInterviews.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-600" />
                  {upcomingInterviews.length > 1 ? 'Upcoming Interviews' : 'Upcoming Interview'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="py-2 text-gray-500">Loading…</div>
                ) : (
                  <div className="space-y-3">
                    {upcomingInterviews.map((iv: any) => (
                      <Link key={iv.id} to={`/jobs/${iv._job?.id}`} className="p-4 rounded-lg border bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200">
                        {/* Left: job and exact date */}
                        <div className="min-w-0">
                          <p className="font-medium sm:truncate">{iv._job?.title} · {iv._job?.company}</p>
                          <p className="text-sm text-gray-600">{new Date(iv.interview_date).toLocaleString()}</p>
                        </div>
                        {/* Middle: friendly relative time */}
                        <div className="mx-0 sm:mx-3 text-center text-sm font-semibold text-gray-800 sm:whitespace-nowrap">
                          {relativeInterviewTime(iv.interview_date)}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Active Job Tracker</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-2 text-gray-500">Loading…</div>
              ) : activeJobs.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {activeJobs.map((job: any) => {
                    const latest = getLatestJobApplicationStatus(job);
                    return (
                    <Link key={job.id} to={`/jobs/${job.id}`} className="p-4 rounded-lg border bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200">
                      <div className="min-w-0">
                        <p className="font-medium sm:truncate">{job.title} · {job.company}</p>
                        <div className="mt-1">
                          <JobApplicationStatusBadge status={latest} />
                        </div>
                      </div>
                    </Link>
                  )})}
                </div>
              ) : (
                <div className="text-gray-500">You haven't added and jobs yet</div>
              )}
            </CardContent>
          </Card>
          {/* Continue placing next cards in same grid to avoid gaps */}
          {/* Todos */}
          <Card className="lg:col-span-1 h-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Upcoming Tasks
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col h-full">
              {isLoading ? (
                <div className="py-4 text-center text-gray-500">Loading todos...</div>
              ) : (
                <div className="flex-grow flex flex-col">
                  <div className="flex-grow">
                    {todosOverdue.length > 0 || todosDueSoon.length > 0 ? (
                      <div className="space-y-3">
                        {[...todosOverdue.map(t => ({ ...t, _bucket: 'overdue' })), ...todosDueSoon.map(t => ({ ...t, _bucket: 'due_soon' }))]
                          .slice(0, 6)
                          .map((todo: any) => (
                            <Link
                              key={todo.id}
                              to={todo.job_id ? `/jobs/${todo.job_id}` : '/todos'}
                              className="p-4 rounded-lg border bg-white flex items-start justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200"
                            >
                              <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                  {todo._bucket === 'overdue' ? (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">Overdue</span>
                                  ) : (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Due Soon</span>
                                  )}
                                  {todo.priority && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${todo.priority === 'high' ? 'bg-red-50 text-red-700' : todo.priority === 'medium' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                                      {todo.priority}
                                    </span>
                                  )}
                                </div>
                                <p className="mt-2 font-medium truncate">{todo.title}</p>
                                {(todo.job_title || todo.job_company) && (
                                  <p className="text-xs text-gray-500 truncate">{todo.job_title}{todo.job_company ? ` · ${todo.job_company}` : ''}</p>
                                )}
                              </div>
                              <div className="ml-3 flex-shrink-0 text-gray-500 text-sm flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {todo.due_date ? new Date(todo.due_date).toLocaleDateString() : '—'}
                              </div>
                            </Link>
                          ))}
                      </div>
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-center text-gray-500 py-6">
                        <p className="mb-4">Add tasks to track your progress</p>
                        <Button asChild>
                          <Link to="/todos">Add a Task</Link>
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="pt-2 text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link to="/todos">View all</Link>
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Interviews */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="w-5 h-5 text-blue-600" />
                Recent Practice Interviews
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-4 text-center text-gray-500">Loading interviews...</div>
              ) : recentInterviews.length > 0 ? (
                <div className="space-y-3">
                  {recentInterviews.map((iv: any) => (
                    <Link key={iv.id} to={`/interview/${iv.id}`} className="p-4 rounded-lg border bg-white flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-200">
                      <div className="min-w-0">
                        <p className="font-medium truncate">Interview • {new Date(iv.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-500 truncate">{(iv.status || '').toUpperCase()}</p>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        {typeof iv.overall_score === 'number' && (iv.status || '').toLowerCase() === 'complete' ? (
                          <span className={`text-2xl font-bold ${getGradeTextColor(iv.overall_score)}`}>
                            {getScoreLabel(iv.overall_score)}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{(iv.status || 'pending')}</span>
                        )}
                      </div>
                    </Link>
                  ))}
                  <div className="pt-1 text-right">
                    <Button asChild size="sm">
                      <Link to="/jobs">Go to jobs</Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="py-6 text-center text-gray-500">No interviews yet. Start one from a job page.</div>
              )}
            </CardContent>
          </Card>

          {/* Pitches */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="w-5 h-5 text-purple-600" />
                Recent Pitches
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-4 text-center text-gray-500">Loading pitches...</div>
              ) : pitches.length > 0 ? (
                <div className="space-y-3">
                  {pitches.map((p: any) => (
                    <Link key={p.id} to={`/pitch/${p.id}`} className="p-4 rounded-lg border bg-white flex items-center justify-between hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-200">
                      <div className="min-w-0">
                        <p className="font-medium truncate">Pitch • {new Date(p.created_at).toLocaleDateString()}</p>
                        <p className="text-xs text-gray-500 truncate">{(p.status || '').toUpperCase()} • {p.pitch_type || 'general'}</p>
                      </div>
                      <div className="ml-3 flex items-center gap-2">
                        {typeof p.overall_score === 'number' && (p.status || '').toLowerCase() === 'complete' ? (
                          <span className={`text-2xl font-bold ${getGradeTextColor(p.overall_score)}`}>
                            {getScoreLabel(p.overall_score)}
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">{(p.status || 'grading')}</span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center text-gray-600">
                  <p className="mb-3">No pitches yet. Practice your "you" pitch to get started.</p>
                  <Button asChild size="sm">
                    <Link to="/pitch/new" className="flex items-center"><Mic className="mr-2 w-4 h-4" />Practice now</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom cards in a consistent two-column grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">

          {/* Recent Jobs */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="py-4 text-center text-gray-500">Loading recent jobs...</div>
              ) : jobs.length > 0 ? (
                <div className="space-y-4">
                  {sortJobsByPriorityAndRecency(jobs).map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <h3 className="font-medium">{job.title}</h3>
                        <p className="text-sm text-gray-500">{job.company}</p>
                        <div className="mt-1">
                          <JobApplicationStatusBadge status={getLatestJobApplicationStatus(job)} />
                        </div>
                      </div>
                      <Button asChild size="sm">
                        <Link to={`/jobs/${job.id}`} className="flex items-center">
                          View
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <p className="text-gray-500 mb-4">No jobs added yet</p>
                  <Button asChild>
                    <Link to="/jobs/new">Add Your First Job</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Dashboard;
